import { useCallback, useRef, useState } from 'react';
import { get } from 'lodash';
import DeviceImportDialog from '../../infrastructure/device/DeviceImportDialog';
import DeleteConfirmDialog from '../../infrastructure/DeleteConfirmDialog';
import DisableDeleteConfirmDialog from '../../infrastructure/DisableDeleteConfirmDialog';
import { useClassifierLogDialog } from '../../infrastructure/device/ClassifierLogDialog';

/**
 * Custom hook to manage all dialogs for monitored infrastructure list.
 * Handles import dialog, classifier log dialog, and delete/disable confirm dialogs.
 *
 * This hook encapsulates the state and behavior for:
 * - Import from CSV dialog
 * - Classifier log viewer dialog
 * - Delete confirmation dialog (for non-discovered items)
 * - Disable/Delete confirmation dialog (for discovered items)
 *
 * The custom action handler determines which delete dialog to show based on
 * whether the selected items include discovered infrastructure.
 *
 * @param {Object} params
 * @param {Function} params.onDeleteConfirmed - Callback when delete is confirmed
 * @param {Function} params.onDisableConfirmed - Callback when disable is confirmed
 * @returns {Object} Dialog state and handlers
 * @returns {JSX.Element} dialogElements - All dialog components to render
 * @returns {Function} customActionHandler - Handler for pre-delete action that shows appropriate dialog
 * @returns {Function} showClassifierLogDialog - Function to open classifier log dialog
 * @returns {Function} setImportDialogOpen - Function to control import dialog visibility
 * @returns {React.MutableRefObject} refresherRef - Ref to store table refresh function
 *
 * @example
 * function MonitoredInfrastructureList() {
 *   const {
 *     dialogElements,
 *     customActionHandler,
 *     showClassifierLogDialog,
 *     setImportDialogOpen,
 *     refresherRef
 *   } = useMonitoredInfrastructureDialogs({
 *     onDeleteConfirmed: handleDelete,
 *     onDisableConfirmed: handleDisable
 *   });
 *
 *   return (
 *     <>
 *       <InfrastructureListBase customActionHandler={customActionHandler} />
 *       {dialogElements}
 *     </>
 *   );
 * }
 */
export function useMonitoredInfrastructureDialogs({ onDeleteConfirmed, onDisableConfirmed }) {
    const [classifierLogDialog, showClassifierLogDialog] = useClassifierLogDialog();
    const [importDialogOpen, setImportDialogOpen] = useState(false);
    const disableDeleteConfirmDialogRef = useRef(null);
    const deleteConfirmDialogRef = useRef(null);
    const refresherRef = useRef(null);

    const customActionHandler = useCallback((response, selectionData) => {
        const idCount = selectionData?.current?.selectionLength;
        const isDiscovered = get(response, 'discovered', false);

        if (isDiscovered) {
            const discoveredCount = get(response, 'uuids.length', 0);
            disableDeleteConfirmDialogRef.current?.open(idCount, discoveredCount);
        } else {
            deleteConfirmDialogRef.current?.open(idCount);
        }
    }, []);

    const dialogElements = (
        <>
            <DeleteConfirmDialog handleDeleteDataTableAction={onDeleteConfirmed} ref={deleteConfirmDialogRef} />
            <DisableDeleteConfirmDialog
                handleDeleteDataTableAction={onDeleteConfirmed}
                handleDisableDataTableAction={onDisableConfirmed}
                ref={disableDeleteConfirmDialogRef}
            />
            {classifierLogDialog}
            {importDialogOpen && (
                <DeviceImportDialog
                    open={importDialogOpen}
                    setOpen={setImportDialogOpen}
                    onImportComplete={refresherRef.current}
                />
            )}
        </>
    );

    return {
        dialogElements,
        customActionHandler,
        showClassifierLogDialog,
        setImportDialogOpen,
        refresherRef,
    };
}
